import type { ComponentType } from "react";
import { routes } from "routing";
import { lazyView, type RouteRender } from "routing/tree";
import i18n from "i18n/i18next";

const { edit } = routes;

type Load = () => Promise<{ default: ComponentType }>;
type Hoc = (Page: ComponentType) => ComponentType;

/**
 * Each group's screens share a FormProvider supplied by a with*() HOC. Pulls the HOC in
 * alongside the screen so both land in the same chunk.
 */
const withHocLazy =
  <M extends Record<K, Hoc>, K extends string>(hocImport: () => Promise<M>, hocKey: K) =>
  (load: Load): Load =>
  () =>
    Promise.all([hocImport(), load()]).then(([hocs, { default: Page }]) => ({ default: hocs[hocKey](Page) }));

const transportItem = withHocLazy(
  () => import("feat/editItinerary/transport/TransportEditRoutes"),
  "withTransportItem",
);
const activityItem = withHocLazy(() => import("feat/editItinerary/activities/activityEditItem"), "withActivityItem");
const todoItem = withHocLazy(() => import("feat/editItinerary/todo/todoEditItem"), "withTodoItem");
const eventInfo = withHocLazy(() => import("feat/editItinerary/eventInfo/event-info-edit"), "withEventInfo");
const distanceExpense = withHocLazy(
  () => import("feat/editItinerary/expenses/forms/distance/DistanceExpenseFormLayout"),
  "withDistanceExpense",
);
const standardExpense = withHocLazy(
  () => import("feat/editItinerary/expenses/forms/standard/StandardExpenseFormLayout"),
  "withStandardExpense",
);

/**
 * The section-scoped leaves (section/sectionItem/sectionItemPage/sectionItemPageItem)
 * share one EditSectionProvider instance, mounted once by the EditSectionRoutes layout
 * route these are nested under in routes.tsx - see withEditSection.tsx. Kept as their
 * own list (rather than folded into editRoutes) so that nesting is possible: renderRoutes
 * emits flat sibling <Route>s, so wrapping a subset in a parent layout Route means
 * rendering that subset separately from the rest of editRoutes.
 */
export const editSectionRoutes: RouteRender[] = [
  lazyView(edit.section, () => import("./sections/edit-sections").then((m) => ({ default: m.EditSectionsPage })), ""),
  lazyView(
    edit.sectionItem,
    () => import("./sections/items/edit-sections-items").then((m) => ({ default: m.EditSectionsItemsPage })),
    "",
  ),
  lazyView(
    edit.sectionItemPage,
    () =>
      import("./sections/items/pages/edit-sections-items-pages").then((m) => ({
        default: m.EditSectionsItemsPagesPage,
      })),
    "",
  ),
  lazyView(
    edit.sectionItemPageItem,
    () =>
      import("./sections/items/pages/edit-sections-items-pages").then((m) => ({
        default: m.EditSectionsItemsPagesPage,
      })),
    "",
  ),
];

/**
 * The edit-itinerary route tree. Each group's stateful FormProvider wraps its
 * flattened leaves individually via a with*() HOC, keyed on the item's id param
 * where one exists - see withEditSection.tsx for why the key matters. (The
 * section-scoped group is the exception - see editSectionRoutes above.)
 */
export const editRoutes: RouteRender[] = [
  lazyView(edit.sectionSelection, () => import("./edit").then((m) => ({ default: m.EditPage })), "Edit Itinerary"),
  lazyView(
    edit.publicItineraries,
    () =>
      import("./public-itineraries/edit-public-itineraries").then((m) => ({ default: m.EditPublicItinerariesPage })),
    () => i18n.t("edit-itinerary.public-itineraries"),
  ),
  lazyView(
    edit.members,
    () =>
      Promise.all([
        import("feat/editItinerary/editMembers/editMembersProvider"),
        import("./members/edit-members"),
      ]).then(([{ EditMembersProvider }, { EditMembersPage }]) => ({
        default: () => (
          <EditMembersProvider>
            <EditMembersPage />
          </EditMembersProvider>
        ),
      })),
    "Manage Team",
  ),
  lazyView(
    edit.membersAdd,
    () => import("./members/add/edit-members-add").then((m) => ({ default: m.EditMembersAddPage })),
    "",
  ),
  lazyView(
    edit.dateTimePicker,
    () => import("./date-time-picker/edit-date-time-picker").then((m) => ({ default: m.EditDateTimePickerPage })),
    "",
  ),
  lazyView(
    edit.transportList,
    () => import("./transport/edit-transport").then((m) => ({ default: m.EditTransportPage })),
    "Transport",
  ),
  lazyView(
    edit.transportItem,
    transportItem(() =>
      import("./transport/items/edit-transport-items").then((m) => ({ default: m.EditTransportItemsPage })),
    ),
    "",
  ),
  lazyView(
    edit.transportPickUpAddress,
    transportItem(() =>
      import("./transport/items/pick-up-address/edit-transport-items-pick-up-address").then((m) => ({
        default: m.EditTransportItemsPickUpAddressPage,
      })),
    ),
    "Pick-up Address",
  ),
  lazyView(
    edit.transportDropOffAddress,
    transportItem(() =>
      import("./transport/items/drop-off-address/edit-transport-items-drop-off-address").then((m) => ({
        default: m.EditTransportItemsDropOffAddressPage,
      })),
    ),
    "Drop-off Address",
  ),
  lazyView(
    edit.transportBookings,
    transportItem(() =>
      import("./transport/items/bookings/edit-transport-items-bookings").then((m) => ({
        default: m.EditTransportItemsBookingsPage,
      })),
    ),
    "Bookings",
  ),
  lazyView(
    edit.transportBooking,
    transportItem(() =>
      import("./transport/items/bookings/items/edit-transport-items-bookings-items").then((m) => ({
        default: m.EditTransportItemsBookingsItemsPage,
      })),
    ),
    "",
  ),
  lazyView(
    edit.activitiesList,
    () => import("./activities/edit-activities").then((m) => ({ default: m.EditActivitiesPage })),
    "Activities",
  ),
  lazyView(
    edit.activityItem,
    activityItem(() =>
      import("./activities/items/edit-activities-items").then((m) => ({ default: m.EditActivitiesItemsPage })),
    ),
    "",
  ),
  lazyView(
    edit.activityAddress,
    activityItem(() =>
      import("./activities/items/address/edit-activities-items-address").then((m) => ({
        default: m.EditActivitiesItemsAddressPage,
      })),
    ),
    "Address",
  ),
  lazyView(
    edit.activityBookings,
    activityItem(() =>
      import("./activities/items/bookings/edit-activities-items-bookings").then((m) => ({
        default: m.EditActivitiesItemsBookingsPage,
      })),
    ),
    "Bookings",
  ),
  lazyView(
    edit.activityBooking,
    activityItem(() =>
      import("./activities/items/bookings/items/edit-activities-items-bookings-items").then((m) => ({
        default: m.EditActivitiesItemsBookingsItemsPage,
      })),
    ),
    "",
  ),
  lazyView(
    edit.todoItem,
    todoItem(() => import("./todos/items/edit-todos-items").then((m) => ({ default: m.EditTodosItemsPage }))),
    "",
  ),
  lazyView(
    edit.todoBookings,
    todoItem(() =>
      import("./todos/items/bookings/edit-todos-items-bookings").then((m) => ({
        default: m.EditTodosItemsBookingsPage,
      })),
    ),
    "Bookings",
  ),
  lazyView(
    edit.todoBooking,
    todoItem(() =>
      import("./todos/items/bookings/items/edit-todos-items-bookings-items").then((m) => ({
        default: m.EditTodosItemsBookingsItemsPage,
      })),
    ),
    "",
  ),
  lazyView(
    edit.eventInfo,
    eventInfo(() => import("./event-info/edit-event-info").then((m) => ({ default: m.EditEventInfoPage }))),
    () => i18n.t("general.edit"),
  ),
  lazyView(
    edit.eventInfoAddress,
    eventInfo(() =>
      import("./event-info/address/edit-event-info-address").then((m) => ({ default: m.EditEventInfoAddressPage })),
    ),
    "Address",
  ),
  lazyView(
    edit.hotelSearch,
    () => import("./hotel-booking/edit-hotel-booking").then((m) => ({ default: m.EditHotelBookingPage })),
    () => i18n.t("edit-itinerary.section.bookHotel"),
  ),
  lazyView(
    edit.hotelDetails,
    () =>
      import("./hotel-booking/details/edit-hotel-booking-details").then((m) => ({
        default: m.EditHotelBookingDetailsPage,
      })),
    "Hotel Details",
  ),
  lazyView(
    edit.hotelAvailability,
    () =>
      import("./hotel-booking/availability/edit-hotel-booking-availability").then((m) => ({
        default: m.EditHotelBookingAvailabilityPage,
      })),
    "Availability",
  ),
  lazyView(
    edit.hotelReturn,
    () =>
      import("./hotel-booking/return/edit-hotel-booking-return").then((m) => ({
        default: m.EditHotelBookingReturnPage,
      })),
    () => i18n.t("booking.addBooking"),
  ),
  lazyView(
    edit.hotelManage,
    () =>
      import("./hotel-booking/items/manage/edit-hotel-booking-items-manage").then((m) => ({
        default: m.EditHotelBookingItemsManagePage,
      })),
    () => i18n.t("booking.manageYourBookings"),
  ),
  lazyView(
    edit.expenses,
    () => import("./expenses/edit-expenses").then((m) => ({ default: m.EditExpensesPage })),
    "Expenses",
  ),
  lazyView(
    edit.expenseNewStandard,
    () =>
      import("./expenses/new/standard/edit-expenses-new-standard").then((m) => ({
        default: m.EditExpensesNewStandardPage,
      })),
    () => i18n.t("cost.addStandardExpense"),
  ),
  lazyView(
    edit.expenseNewDistance,
    distanceExpense(() =>
      import("./expenses/new/distance/edit-expenses-new-distance").then((m) => ({
        default: m.EditExpensesNewDistancePage,
      })),
    ),
    () => i18n.t("cost.addDistanceExpense"),
  ),
  lazyView(
    edit.expenseNewDistanceAddress,
    distanceExpense(() =>
      import("./expenses/address/edit-expenses-distance-address").then((m) => ({
        default: m.EditExpensesDistanceAddressPage,
      })),
    ),
    "Address",
  ),
  lazyView(
    edit.expenseEditStandard,
    () =>
      import("./expenses/items/standard/edit-expenses-items-standard").then((m) => ({
        default: m.EditExpensesItemsStandardPage,
      })),
    "Edit Expense",
  ),
  lazyView(
    edit.expenseEditDistance,
    distanceExpense(() =>
      import("./expenses/items/distance/edit-expenses-items-distance").then((m) => ({
        default: m.EditExpensesItemsDistancePage,
      })),
    ),
    "Edit Expense",
  ),
  lazyView(
    edit.expenseEditDistanceAddress,
    distanceExpense(() =>
      import("./expenses/address/edit-expenses-distance-address").then((m) => ({
        default: m.EditExpensesDistanceAddressPage,
      })),
    ),
    "Address",
  ),
  lazyView(
    edit.expenseEditStandardAIResult,
    standardExpense(() =>
      import("../../feat/editItinerary/expenses/ai-expense-overview-dialog/AIExpenseSheet").then((m) => ({
        default: m.default,
      })),
    ),
    () => i18n.t("cost.ai.dialog-title"),
  ),
];
